import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FiImage } from 'react-icons/fi';
import GodViewMediaModal from './GodViewMediaModal.jsx';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-SG');
};

const formatPhoneList = (arr, field) => {
    if (!Array.isArray(arr) || !arr.length) return '—';
    const values = arr
        .map((entry) => (typeof entry === 'string' ? entry : entry?.[field] || entry?.contactNumber || entry?.whatsAppNumber))
        .filter(Boolean);
    return values.length ? values.join(', ') : '—';
};

const NonMemberDetailsModal = ({ nonMember, onClose, detailsLoading = false, onUpdated }) => {
    const [showMediaModal, setShowMediaModal] = useState(false);

    if (!nonMember) return null;

    return (
        <>
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-start justify-center pt-20 px-4 pb-4 overflow-x-hidden overflow-y-auto min-h-full z-50">
                <div className="relative mx-auto p-4 border max-w-3xl w-full shadow-lg rounded-md bg-white overflow-hidden flex flex-col max-h-[calc(100vh-5rem)]">
                    <div className="flex justify-between items-center border-b pb-4">
                        <div className="flex items-center gap-3">
                            <h3 className="text-xl font-medium">
                                Non-Member Details —{' '}
                                <span className="text-blue-700">{nonMember.name || '—'}</span>
                            </h3>
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                {nonMember.newSetupDone ? 'Setup done' : 'Setup pending'}
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            {nonMember._id && (
                                <button
                                    type="button"
                                    onClick={() => setShowMediaModal(true)}
                                    className="flex items-center gap-1.5 px-2 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                >
                                    <FiImage className="w-4 h-4" />
                                    Media
                                </button>
                            )}
                            <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">
                                ×
                            </button>
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-1 pt-4 space-y-6 text-sm">
                        <section>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Profile</h4>
                            <div className="grid grid-cols-2 gap-3 text-blue-700">
                                <p><span className="text-gray-600">Web ID:</span> {nonMember.webID || '—'}</p>
                                <p><span className="text-gray-600">Nationality:</span> {nonMember.nationality || '—'}</p>
                                <p><span className="text-gray-600">Age (DOB):</span> {formatDate(nonMember.age)}</p>
                                <p><span className="text-gray-600">Region:</span> {nonMember.region || '—'}</p>
                                <p><span className="text-gray-600">Area:</span> {nonMember.area || '—'}</p>
                                <p><span className="text-gray-600">Hours:</span> {nonMember.openHour || '—'} – {nonMember.closeHour || '—'}</p>
                            </div>
                        </section>

                        <section>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Contact</h4>
                            <div className="text-blue-700 space-y-1">
                                <p><span className="text-gray-600">Phone:</span> {formatPhoneList(nonMember.contactNumbers, 'contactNumber')}</p>
                                <p><span className="text-gray-600">WhatsApp:</span> {formatPhoneList(nonMember.whatsappNumbers, 'whatsAppNumber')}</p>
                            </div>
                        </section>

                        <section>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Listing</h4>
                            <div className="text-blue-700 space-y-1">
                                <p><span className="text-gray-600">Website:</span> {nonMember.website || '—'}</p>
                                <p><span className="text-gray-600">Price / hr:</span> {nonMember.priceHr != null ? `$${Number(nonMember.priceHr).toFixed(2)}` : '—'}</p>
                                <p><span className="text-gray-600">Service:</span> {(nonMember.service || []).join(', ') || '—'}</p>
                                {nonMember.extraService && (
                                    <p><span className="text-gray-600">Extra:</span> {nonMember.extraService}</p>
                                )}
                                {nonMember.websiteRemarks && (
                                    <p><span className="text-gray-600">Website remarks:</span> {nonMember.websiteRemarks}</p>
                                )}
                            </div>
                        </section>

                        {nonMember.remarks && (
                            <section>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Remarks</h4>
                                <p className="text-blue-700 whitespace-pre-wrap">{nonMember.remarks}</p>
                            </section>
                        )}

                        <section className="text-gray-500 text-xs border-t pt-3">
                            <p>Created: {formatDate(nonMember.createdAt)} · Updated: {formatDate(nonMember.updatedAt)}</p>
                        </section>
                    </div>

                    {detailsLoading && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10">
                            <div className="text-center text-gray-600">
                                <div className="inline-block w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-2" />
                                <p className="text-sm font-medium">Loading details…</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showMediaModal && (
                <GodViewMediaModal
                    member={nonMember}
                    apiResource="nonmembers"
                    onClose={() => setShowMediaModal(false)}
                    onMediaUpdate={onUpdated}
                />
            )}
        </>
    );
};

NonMemberDetailsModal.propTypes = {
    nonMember: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    detailsLoading: PropTypes.bool,
    onUpdated: PropTypes.func,
};

export default NonMemberDetailsModal;
